import React from 'react';
import styles from './TermsAndConditions.module.css';

const TermsAndConditions = ({ onAccept }) => {
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <img src="/auth/logo.svg" alt="Movemend Logo" className={styles.logo} />
          <h2 className={styles.title}>Terms of Service & Privacy Policy</h2>
          <p className={styles.effectiveDate}>(Effective September 22, 2025)</p>
        </div>
        
        <div className={styles.scrollContainer}>
          <div className={styles.introText}>
            <p>Welcome to Movemend - a gamified exercise platform designed to support your therapy program. By using Movemend, you agree to these Terms of Service ("Terms") and acknowledge our Privacy Policy. If you do not agree, please do not use Movemend.</p>
          </div>

          <section className={styles.section}>
            <h3>1. Who Can Use Movemend</h3>
            <div className={styles.subsection}>
              <p><strong>Patients:</strong> You must be assigned a Movemend program by a licensed physical or occupational therapist involved in your care. If you are under 18 or unable to consent, a parent, guardian, or legal representative must accept these Terms on your behalf.</p>
              <p><strong>Therapists/Staff:</strong> You must be authorized to provide or support patient programs as part of the therapy team.</p>
              <p><strong>Disclaimer:</strong> Movemend is not a medical device and does not replace the advice, diagnosis, or judgment of your licensed healthcare provider. Your therapist and facility remain responsible for your care plan and clinical decisions.</p>
            </div>
          </section>

          <section className={styles.section}>
            <h3>2. Acceptable Use</h3>
            <p>You agree to:</p>
            <ul>
              <li>Use Movemend only for its intended purpose - to participate in assigned exercises and track progress.</li>
              <li>Keep any login codes or credentials private.</li>
              <li>Not attempt to access other users' information or interfere with the system.</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h3>3. Safety, Disclaimers & Limitations</h3>
            <ul>
              <li>Movemend tracks your movements during exercises but does not monitor your medical condition, symptoms, or overall health in real time. Always follow your therapist's guidance on safe participation.</li>
              <li>If you feel pain, dizziness, or other concerning symptoms, stop exercising immediately and notify your therapist or facility staff.</li>
              <li>Movemend is provided "as is," without warranties of any kind.</li>
              <li>We are not responsible for injury or harm resulting from incorrect exercise technique, failure to follow therapist instructions, or device misuse.</li>
              <li>To the fullest extent permitted by law, Movemend and its affiliates are not liable for indirect, incidental, or consequential damages.</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h3>4. Privacy & Data Use</h3>
            <p>We take your privacy seriously. This Privacy Policy explains how we collect, use, and protect your information.</p>
            
            <div className={styles.subsection}>
              <h4>A. What We Collect</h4>
              <p><strong>Patients:</strong> First name + last initial, room/bed number, therapist assignment, exercise performance data (e.g., range of motion, session time, accuracy scores).</p>
              <p><strong>Therapists:</strong> Name, professional role, login credentials, exercise program assignments.</p>
            </div>

            <div className={styles.subsection}>
              <h4>B. How We Use Your Information</h4>
              <ul>
                <li>Provide and manage your Movemend exercise program.</li>
                <li>Track your progress and share results with your therapy team.</li>
                <li>Improve our platform and develop new features.</li>
              </ul>
            </div>

            <div className={styles.subsection}>
              <h4>C. De-Identified Data</h4>
              <ul>
                <li>We may remove all identifiers from your data and combine it with other users' information to create "de-identified" or aggregated data.</li>
                <li>De-identified data is not considered PHI under HIPAA.</li>
                <li>We may use this data for research, analytics, or sharing program results (e.g., "92% of participants met their exercise goals") without further permission.</li>
              </ul>
            </div>

            <div className={styles.subsection}>
              <h4>D. Who We Share Your Data With</h4>
              <ul>
                <li>Your therapy team and facility staff directly involved in your care.</li>
                <li>Our technology partners who help us deliver Movemend (all bound by confidentiality and HIPAA requirements).</li>
                <li>We do not sell your personal information to third parties.</li>
              </ul>
            </div>

            <div className={styles.subsection}>
              <h4>E. Data Security</h4>
              <ul>
                <li>Data is encrypted during transmission and while stored.</li>
                <li>Access is restricted to authorized personnel only.</li>
              </ul>
            </div>

            <div className={styles.subsection}>
              <h4>F. Data Retention</h4>
              <ul>
                <li>Your personal information is retained only as long as needed to support your care or as required by law.</li>
                <li>De-identified data may be kept indefinitely for research and analytics.</li>
              </ul>
            </div>
          </section>

          <section className={styles.section}>
            <h3>5. Your Choices</h3>
            <ul>
              <li>You can ask to see or correct your information by speaking with your therapy team.</li>
              <li>You can stop using Movemend at any time, but your therapist may still use your past records to support your care.</li>
              <li>If you would like us to use your comments, stories, photos, or videos for marketing, you must sign a separate HIPAA authorization form.</li>
            </ul>
          </section>

          <section className={styles.section}>
            <h3>6. HIPAA Notice</h3>
            <p>Movemend and your facility are committed to protecting your Protected Health Information (PHI) in accordance with HIPAA. Your HIPAA rights remain intact when using Movemend.</p>
          </section>

          <section className={styles.section}>
            <h3>7. Governing Law</h3>
            <p>These Terms are governed by the laws of the State of New York, without regard to conflict of law rules.</p>
          </section>

          <section className={styles.section}>
            <h3>8. Contact Us</h3>
            <p>Questions about these Terms or our Privacy Policy?</p>
            <p>Email: <a href="mailto:info@movemendhealth.com">info@movemendhealth.com</a></p>
          </section>

          <section className={styles.acceptanceSection}>
            <h3>Acceptance</h3>
            <p>By selecting "I Agree" or continuing to use Movemend, you confirm that you understand and accept these Terms and our Privacy Policy.</p>
            <p>Therapists confirm that they are authorized to use Movemend for patient care within their professional role.</p>
          </section>
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.acceptButton} onClick={onAccept}>
            I Agree
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
